import React, { useCallback, useMemo, useState } from "react";
import {
  Page,
  Card,
  TextField,
  BlockStack,
  Text,
  Banner,
} from "@shopify/polaris";
import PageLoader from "../../components/PageLoader";
import { useAppTitleBar } from "../../hooks/useAppTitleBar";
import { SaveBar, useAppBridge } from "@shopify/app-bridge-react";
import { saveAppSettings } from "../../helper/settingsApi";
import { getCachedAppSettings } from "../../helper/settingsCache";
import { useSettingsFromSession } from "../../hooks/useAppSessionData";

const SETTINGS_SAVE_BAR_ID = "calendar-app-settings-save-bar";

function settingsToJson(customCss, customJs) {
  return JSON.stringify({ customCss: customCss || "", customJs: customJs || "" });
}

function initialSettingsState() {
  const cached = getCachedAppSettings();
  if (!cached) {
    return { customCss: "", customJs: "", baselineJson: null, loading: true };
  }
  const css = cached.customCss || "";
  const js = cached.customJs || "";
  return {
    customCss: css,
    customJs: js,
    baselineJson: settingsToJson(css, js),
    loading: false,
  };
}

export default function SettingsPage() {
  const shopify = useAppBridge();
  const initial = useMemo(() => initialSettingsState(), []);
  const {
    customCss,
    setCustomCss,
    customJs,
    setCustomJs,
    baselineJson,
    setBaselineJson,
    loading,
    error,
    setError,
  } = useSettingsFromSession(initial);
  const [saving, setSaving] = useState(false);
  const [syncWarning, setSyncWarning] = useState(null);

  useAppTitleBar({ title: "Settings" });

  const isDirty = useMemo(() => {
    if (baselineJson == null) return false;
    try {
      return baselineJson !== settingsToJson(customCss, customJs);
    } catch {
      return true;
    }
  }, [baselineJson, customCss, customJs]);

  const onSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setSyncWarning(null);
    try {
      const result = await saveAppSettings({ customCss, customJs });
      const saved = result?.settings || { customCss, customJs };
      setBaselineJson(settingsToJson(saved.customCss, saved.customJs));
      if (result?.metafield_sync_error) {
        setSyncWarning(
          `Saved to database. Storefront sync failed: ${result.metafield_sync_error}`,
        );
      }
      try {
        await shopify?.saveBar?.hide?.(SETTINGS_SAVE_BAR_ID);
      } catch {
        /* optional */
      }
    } catch (e) {
      setError(e?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }, [saving, customCss, customJs, shopify, setBaselineJson, setError]);

  const onDiscard = useCallback(() => {
    if (baselineJson == null) return;
    try {
      const parsed = JSON.parse(baselineJson);
      setCustomCss(parsed.customCss || "");
      setCustomJs(parsed.customJs || "");
    } catch {
      /* no-op */
    }
    void shopify?.saveBar?.hide?.(SETTINGS_SAVE_BAR_ID);
  }, [baselineJson, shopify, setCustomCss, setCustomJs]);

  return (
    <Page>
      {loading && !baselineJson ? <PageLoader label="Loading settings" /> : null}
      {!loading || baselineJson ? (
        <>
          <SaveBar id={SETTINGS_SAVE_BAR_ID} open={isDirty}>
            <button
              variant="primary"
              loading={saving ? "" : undefined}
              disabled={saving}
              onClick={(e) => {
                e.preventDefault();
                onSave();
              }}
            >
              Save
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                onDiscard();
              }}
              disabled={saving}
            >
              Discard
            </button>
          </SaveBar>

          <BlockStack gap="400">
            {error ? (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            ) : null}
            {syncWarning ? (
              <Banner tone="warning" onDismiss={() => setSyncWarning(null)}>
                {syncWarning}
              </Banner>
            ) : null}

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingSm">
                  Developers
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Custom CSS and JavaScript apply to every calendar widget on your storefront. Per-widget
                  styling stays in the widget editor (templates, appearance, events).
                </Text>
                <TextField
                  label="Custom CSS"
                  value={customCss}
                  onChange={setCustomCss}
                  multiline={10}
                  autoComplete="off"
                  helpText="Injected once for the whole shop (all calendar blocks)."
                />
                <TextField
                  label="Custom JavaScript"
                  value={customJs}
                  onChange={setCustomJs}
                  multiline={10}
                  autoComplete="off"
                  helpText="Runs once per page load for calendar embeds. Use carefully on live stores."
                />
              </BlockStack>
            </Card>
          </BlockStack>
        </>
      ) : null}
    </Page>
  );
}
